import '@material-symbols/font-400/outlined.css';
// Vuetify's typeface: the page never loaded it, so the chrome fell back to Arial / Helvetica; the latin subset covers every
// locale of the app, and the parent stylesheets cloned into the iframe bring the 500 / 700 weights the legacy lacks.
import '@fontsource/roboto/latin-400.css';
import '@fontsource/roboto/latin-500.css';
import '@fontsource/roboto/latin-700.css';
import 'vuetify/styles';
import { h } from 'vue';
import { createRulesPlugin, createVuetify } from 'vuetify';
import { aliases } from 'vuetify/iconsets/md';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { fr, en, es, pt, nl, de } from 'vuetify/locale';

const availableLocales = { fr, en, es, pt, nl, de };

const LOCALE_MAP = { 'fr-FR': 'fr', 'en-GB': 'en', 'es-ES': 'es', 'pt-PT': 'pt', 'nl-NL': 'nl', 'de-DE': 'de' };

export const resolveVuetifyLocale = (rawLocale) => {
	if (!rawLocale) return navigator.language?.split('-')[0] in availableLocales ? navigator.language.split('-')[0] : 'fr';
	return (LOCALE_MAP[rawLocale] ?? rawLocale.split('-')[0] in availableLocales) ? rawLocale.split('-')[0] : 'fr';
};

// Same ligature mechanism as Vuetify's built-in `md` iconset, but emitting the
// Material Symbols class instead of `material-icons`, so Vuetify's own icons
// ($dropdown, $clear, $checkboxOn...) render with the same font as the app's
// explicit <v-icon> usages and as the legacy iframe (MaterialSymbolsOutlined.css).
const materialSymbols = {
	component: {
		name: 'VMaterialSymbolIcon',
		props: {
			icon: [String, Array, Object, Function],
			tag: { type: [String, Object, Function], required: true },
		},
		setup: (props) => () => h(props.tag, { class: 'material-symbols-outlined' }, props.icon),
	},
};

const myCustomTheme = {
	dark: false,
	colors: {
		primary: '#1c79bb',
		'primary-darken-1': '#155e93',
		secondary: '#00aeff',
		'secondary-darken-1': '#0084ff',
		tertiary: '#61e167',
		background: '#FFFFFF',
		surface: '#fcfcfc',
		'surface-variant': '#f0f4f9',
		'on-surface-variant': '#1d1b20',
		error: '#E7003E',
		info: '#1c79bb',
		success: '#4CAF50',
		warning: '#ff5722',
		'on-primary': '#ffffff',
		'on-secondary': '#ffffff',
		'on-tertiary': '#ffffff',
		'on-background': '#1d1b20',
		'on-surface': '#1d1b20',
		'on-error': '#ffffff',
		'on-info': '#ffffff',
		'on-success': '#ffffff',
		'on-warning': '#ffffff',
		// 1f1f1f
		// 444746
	},
};

export function createMyVuetify(userLocale = 'fr') {
	const vuetify = createVuetify({
		components,
		directives,

		icons: {
			defaultSet: 'md',
			aliases,
			sets: {
				md: materialSymbols,
			},
		},

		locale: {
			locale: userLocale,
			fallback: 'fr',
			messages: availableLocales,
		},

		theme: {
			defaultTheme: 'myCustomTheme',
			themes: {
				myCustomTheme,
			},
		},

		defaults: {
			VDialog: {
				scrollable: true,
			},
			VChip: {
				variant: 'tonal',
			},
			VSelect: {
				density: 'compact',
				hideDetails: 'auto',
			},
			VTextField: {
				density: 'compact',
				hideDetails: 'auto',
			},
			VTextarea: {
				hideDetails: 'auto',
			},
			VList: {
				density: 'compact',
			},
			VBtn: {
				variant: 'tonal',
				rounded: 'xl',
			},
			VBottomSheet: {
				scrollable: true,
				scrim: true,
			},
			VTab: {
				rounded: null,
			},
		},
	});
	const rules = createRulesPlugin({}, vuetify.locale);

	return {
		...vuetify,
		install: (app) => {
			app.use(vuetify);
			app.use(rules);
		},
	};
}
